import React, { useState } from 'react';
import { fdApi } from '../services/api';
import '../styles/Modal.css';

function GenerateFDV2Modal({ entity, onClose, onComplete }) {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [interestStatementFile, setInterestStatementFile] = useState(null);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [referenceDate, setReferenceDate] = useState('');
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [outputBlob, setOutputBlob] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    addFiles(files);
  };

  const addFiles = (files) => {
    // Accept PDF, TXT, and HTML files
    const validFiles = files.filter(file => {
      const fileName = file.name.toLowerCase();
      return fileName.endsWith('.pdf') || fileName.endsWith('.txt') || fileName.endsWith('.html') || fileName.endsWith('.htm');
    });

    if (validFiles.length === 0) {
      setError('Please select PDF, TXT, or HTML files only');
      return;
    }

    if (validFiles.length !== files.length) {
      setError(`Skipped ${files.length - validFiles.length} unsupported file(s). Only PDF, TXT, and HTML files are accepted.`);
      setTimeout(() => setError(null), 3000);
    }

    setSelectedFiles(prev => [...prev, ...validFiles]);
    setError(null);
  };

  const removeFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const files = Array.from(e.dataTransfer.files);
      addFiles(files);
    }
  };

  const handleGenerate = async () => {
    if (selectedFiles.length === 0) {
      setError('Please select at least one file (PDF, TXT, or HTML)');
      return;
    }
    if (!fromDate) {
      setError('Please enter From Date');
      return;
    }
    if (!toDate) {
      setError('Please enter To Date');
      return;
    }

    setGenerating(true);
    setError(null);
    setResult(null);

    try {
      const data = await fdApi.generateFDV2(
        entity.name,
        fromDate,
        toDate,
        referenceDate || toDate, // Use toDate as reference if not specified
        selectedFiles,
        interestStatementFile
      );
      setResult(data);

      // Fetch the output file for immediate download option
      const blob = await fdApi.downloadFDV2Output(
        entity.name,
        fromDate,
        toDate,
        data.timestamp
      );
      setOutputBlob(blob);
    } catch (err) {
      const errorMessage = err.message || 'Failed to generate FD V2 schedule';

      // Add helpful context for common errors
      if (errorMessage.includes('No FD data available')) {
        setError(
          <div>
            <strong>❌ No Fixed Deposit data could be extracted from the uploaded PDFs.</strong>
            <div style={{ marginTop: '10px', fontSize: '0.95em' }}>
              <strong>📋 Possible reasons:</strong>
              <ul style={{ marginTop: '5px', marginBottom: '5px', paddingLeft: '20px' }}>
                <li>The PDFs do not contain Fixed Deposit information</li>
                <li>The PDFs are scanned images without text layer</li>
                <li>The bank statement format is not recognized by AI</li>
                <li>The FD information is in an unexpected format</li>
              </ul>
              <strong>💡 Suggestions:</strong>
              <ul style={{ marginTop: '5px', marginBottom: '5px', paddingLeft: '20px' }}>
                <li>Verify the PDFs contain FD/Term Deposit information</li>
                <li>Try exporting PDFs directly from bank portals (not scanned copies)</li>
                <li>Upload FD confirmation letters or certificates</li>
                <li>Ensure the bank name and FD details are clearly visible</li>
              </ul>
              <strong>🔧 For Scanned PDFs:</strong>
              <ul style={{ marginTop: '5px', marginBottom: '0', paddingLeft: '20px' }}>
                <li>The system can handle scanned PDFs with OCR (Optical Character Recognition)</li>
                <li>Contact your administrator to enable OCR support if not already available</li>
                <li>OCR works best with high-quality scans (300 DPI or higher)</li>
              </ul>
            </div>
          </div>
        );
      } else {
        setError(errorMessage);
      }
    } finally {
      setGenerating(false);
    }
  };

  const handleDownload = () => {
    if (!outputBlob || !result) return;

    const url = window.URL.createObjectURL(outputBlob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `fd_v2_schedule_${entity.name}_${fromDate}_to_${toDate}_${result.timestamp}.xlsx`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  const handleClose = () => {
    if (result && onComplete) {
      onComplete();
    } else {
      onClose();
    }
  };

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '700px' }}>
        <div className="modal-header">
          <h2>Generate FD Schedule V2 (AI-Powered)</h2>
          <button className="modal-close" onClick={handleClose}>
            &times;
          </button>
        </div>

        <div className="modal-body">
          {error && <div className="alert alert-error">{error}</div>}

          {result && (
            <div className="alert alert-success">
              <strong>✓ Successfully generated FD V2 schedule!</strong>
              <ul style={{ marginTop: '10px', paddingLeft: '20px', marginBottom: '0' }}>
                <li><strong>Entity:</strong> {result.entity_name}</li>
                <li><strong>Period:</strong> {result.from_date} to {result.to_date}</li>
                <li><strong>Reference Date:</strong> {result.reference_date}</li>
              </ul>

              <div style={{ marginTop: '15px', padding: '10px', backgroundColor: '#e3f2fd', borderRadius: '4px', borderLeft: '4px solid #2196F3' }}>
                <strong>Processing Summary:</strong>
                <ul style={{ marginTop: '5px', paddingLeft: '20px', marginBottom: '0' }}>
                  <li>Files Processed: {result.processing.successful} / {result.processing.total_files}</li>
                  {result.processing.failed > 0 && (
                    <li style={{ color: '#ff9800' }}>⚠ Failed Files: {result.processing.failed}</li>
                  )}
                </ul>
              </div>

              <div style={{ marginTop: '10px', padding: '10px', backgroundColor: '#f1f8e9', borderRadius: '4px', borderLeft: '4px solid #8bc34a' }}>
                <strong>Extraction Results:</strong>
                <ul style={{ marginTop: '5px', paddingLeft: '20px', marginBottom: '0' }}>
                  <li><strong>Total FDs Extracted:</strong> {result.summary.total_fds}</li>
                  <li><strong>Total Principal:</strong> ₹ {result.summary.total_principal.toLocaleString('en-IN', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })}</li>
                  <li><strong>Total Lien Amount:</strong> ₹ {result.summary.total_lien.toLocaleString('en-IN', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })}</li>
                  {result.summary.interest_statement && (
                    <li style={{ color: '#2e7d32' }}>
                      <strong>Interest Statement:</strong> {result.summary.interest_statement.matched_fds} FDs matched,
                      total ₹ {parseFloat(result.summary.interest_statement.total_interest_received || 0).toLocaleString('en-IN', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })}
                    </li>
                  )}
                </ul>
              </div>

              {result.summary.by_bank && Object.keys(result.summary.by_bank).length > 0 && (
                <div style={{ marginTop: '15px', padding: '10px', backgroundColor: '#f0f8ff', borderRadius: '4px' }}>
                  <strong>By Bank:</strong>
                  <ul style={{ marginTop: '5px', paddingLeft: '20px' }}>
                    {Object.entries(result.summary.by_bank).map(([bank, amount]) => (
                      <li key={bank}>
                        {bank}: ₹ {parseFloat(amount).toLocaleString('en-IN', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        })}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {result.summary.by_classification && Object.keys(result.summary.by_classification).length > 0 && (
                <div style={{ marginTop: '10px', padding: '10px', backgroundColor: '#e8f5e9', borderRadius: '4px' }}>
                  <strong>Classification (Ind AS):</strong>
                  <ul style={{ marginTop: '5px', paddingLeft: '20px' }}>
                    {Object.entries(result.summary.by_classification).map(([classification, amount]) => (
                      <li key={classification}>
                        {classification}: ₹ {parseFloat(amount).toLocaleString('en-IN', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        })}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <button
                className="btn btn-primary"
                onClick={handleDownload}
                style={{ marginTop: '15px' }}
              >
                Download Schedule
              </button>
            </div>
          )}

          {!result && (
            <>
              <div className="alert alert-info" style={{ marginBottom: '15px' }}>
                <strong>✨ AI-Powered FD Extraction</strong>
                <p style={{ marginTop: '5px', marginBottom: '10px' }}>
                  Upload multiple bank statement files (PDF, TXT, or HTML). AI will automatically extract FD data,
                  classify per Ind AS Schedule 3, and generate a comprehensive Excel schedule.
                </p>
                <div style={{ fontSize: '0.9em', backgroundColor: '#fff', padding: '8px', borderRadius: '4px', marginTop: '10px' }}>
                  <strong>📋 Supported Documents:</strong>
                  <ul style={{ marginTop: '5px', marginBottom: '0', paddingLeft: '20px' }}>
                    <li>Bank statements with FD/Term Deposit details (PDF, HTML)</li>
                    <li>Balance confirmation certificates (PDF, TXT, HTML)</li>
                    <li>Fixed Deposit confirmation letters</li>
                    <li>FD certificates or advices</li>
                    <li>Interest certificates with TDS details</li>
                    <li>HDFC HTML statements (exported from FlexCube)</li>
                  </ul>
                </div>
              </div>

              <p>Generate FD schedule for <strong>{entity.name}</strong></p>

              {/* Date Inputs */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
                <div className="form-group">
                  <label htmlFor="from-date">From Date *</label>
                  <input
                    type="date"
                    id="from-date"
                    className="form-control"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    disabled={generating}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="to-date">To Date *</label>
                  <input
                    type="date"
                    id="to-date"
                    className="form-control"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    disabled={generating}
                  />
                </div>
              </div>

              <div className="form-group" style={{ marginBottom: '15px' }}>
                <label htmlFor="reference-date">Reference Date (for classification)</label>
                <input
                  type="date"
                  id="reference-date"
                  className="form-control"
                  value={referenceDate}
                  onChange={(e) => setReferenceDate(e.target.value)}
                  disabled={generating}
                  placeholder="Optional - defaults to To Date"
                />
                <small className="form-text">
                  FDs will be classified based on maturity from this date
                </small>
              </div>

              {/* File Upload Area */}
              <div
                className={`file-upload-area ${dragActive ? 'drag-active' : ''}`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                style={{ marginBottom: '15px' }}
              >
                <input
                  type="file"
                  id="fd-v2-file-input"
                  accept=".pdf,.txt,.html,.htm"
                  multiple
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                />
                <label htmlFor="fd-v2-file-input" className="file-upload-label">
                  {selectedFiles.length === 0 ? (
                    <>
                      <div className="file-icon">📁</div>
                      <div>Click to browse or drag and drop</div>
                      <div className="file-hint">PDF, TXT, and HTML files supported - Multiple files allowed</div>
                    </>
                  ) : (
                    <>
                      <div className="file-icon">📄</div>
                      <div>{selectedFiles.length} file(s) selected</div>
                      <div className="file-hint">Click or drag to add more files</div>
                    </>
                  )}
                </label>
              </div>

              {/* Selected Files List */}
              {selectedFiles.length > 0 && (
                <div style={{ marginBottom: '15px' }}>
                  <strong>Selected Files:</strong>
                  <div style={{ maxHeight: '200px', overflowY: 'auto', marginTop: '10px' }}>
                    {selectedFiles.map((file, index) => {
                      const fileName = file.name.toLowerCase();
                      const isPdf = fileName.endsWith('.pdf');
                      const isHtml = fileName.endsWith('.html') || fileName.endsWith('.htm');
                      const fileIcon = isPdf ? '📄' : (isHtml ? '🌐' : '📝');
                      const fileType = isPdf ? 'PDF' : (isHtml ? 'HTML' : 'TXT');
                      const fileColor = isPdf ? '#d32f2f' : (isHtml ? '#f57c00' : '#1976d2');
                      const fileBgColor = isPdf ? '#ffebee' : (isHtml ? '#fff3e0' : '#e3f2fd');
                      return (
                        <div
                          key={index}
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '8px 12px',
                            backgroundColor: '#f8f9fa',
                            borderRadius: '4px',
                            marginBottom: '8px'
                          }}
                        >
                          <div style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            <span style={{ marginRight: '8px' }}>{fileIcon}</span>
                            <strong>{index + 1}.</strong> {file.name}
                            <span style={{
                              marginLeft: '10px',
                              color: fileColor,
                              fontSize: '0.8em',
                              fontWeight: 'bold',
                              padding: '2px 6px',
                              backgroundColor: fileBgColor,
                              borderRadius: '3px'
                            }}>
                              {fileType}
                            </span>
                            <span style={{ marginLeft: '10px', color: '#666', fontSize: '0.9em' }}>
                              ({(file.size / 1024).toFixed(1)} KB)
                            </span>
                          </div>
                          <button
                            className="btn btn-sm btn-secondary"
                            onClick={() => removeFile(index)}
                            disabled={generating}
                            style={{ marginLeft: '10px' }}
                          >
                            Remove
                          </button>
                        </div>
                      );
                    })}
                  </div>
                  <button
                    className="btn btn-secondary"
                    onClick={() => setSelectedFiles([])}
                    disabled={generating}
                    style={{ marginTop: '10px' }}
                  >
                    Clear All
                  </button>
                </div>
              )}

              {/* HDFC Interest Statement Upload (optional) */}
              <div style={{
                border: '1px solid #e0e0e0',
                borderRadius: '6px',
                padding: '15px',
                marginBottom: '15px',
                backgroundColor: '#fafafa'
              }}>
                <div style={{ fontWeight: 'bold', marginBottom: '8px', color: '#333' }}>
                  HDFC Interest Statement (Optional)
                </div>
                <div style={{ fontSize: '0.9em', color: '#666', marginBottom: '10px' }}>
                  Upload the HDFC bank transaction Excel file (e.g., "TTL Interest recd in HDFC bank.xlsx") to populate the <strong>Interest Received</strong> column in FD Details.
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                  <input
                    type="file"
                    id="interest-statement-input"
                    accept=".xlsx"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        if (!file.name.toLowerCase().endsWith('.xlsx')) {
                          setError('Interest statement must be an Excel (.xlsx) file');
                          return;
                        }
                        setInterestStatementFile(file);
                      }
                    }}
                    style={{ display: 'none' }}
                    disabled={generating}
                  />
                  <label
                    htmlFor="interest-statement-input"
                    className="btn btn-secondary"
                    style={{ cursor: generating ? 'not-allowed' : 'pointer', marginBottom: 0 }}
                  >
                    Browse Excel File
                  </label>
                  {interestStatementFile ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                      <span style={{
                        padding: '4px 10px',
                        backgroundColor: '#e8f5e9',
                        borderRadius: '4px',
                        color: '#2e7d32',
                        fontSize: '0.9em',
                        fontWeight: 'bold'
                      }}>
                        XLSX
                      </span>
                      <span style={{ fontSize: '0.9em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {interestStatementFile.name}
                      </span>
                      <span style={{ color: '#888', fontSize: '0.85em', whiteSpace: 'nowrap' }}>
                        ({(interestStatementFile.size / 1024).toFixed(1)} KB)
                      </span>
                      <button
                        className="btn btn-sm btn-secondary"
                        onClick={() => setInterestStatementFile(null)}
                        disabled={generating}
                        style={{ marginLeft: 'auto' }}
                      >
                        Remove
                      </button>
                    </div>
                  ) : (
                    <span style={{ color: '#999', fontSize: '0.9em' }}>No file selected</span>
                  )}
                </div>
              </div>

              <div className="alert alert-info">
                <strong>Output includes:</strong>
                <ul style={{ marginTop: '10px', paddingLeft: '20px', marginBottom: '0' }}>
                  <li>Summary by Bank</li>
                  <li>FD Details: Principal, Lien, Gross Interest, Interest Accrued, TDS, Interest Received</li>
                  <li>Bank Guarantees (if available)</li>
                  <li>Ind AS Schedule 3 Classification Summary</li>
                </ul>
              </div>
            </>
          )}
        </div>

        <div className="modal-footer">
          {!result && (
            <button
              className="btn btn-primary"
              onClick={handleGenerate}
              disabled={selectedFiles.length === 0 || !fromDate || !toDate || generating}
            >
              {generating ? 'Generating... (this may take a few minutes)' : 'Generate FD Schedule'}
            </button>
          )}
          <button className="btn btn-secondary" onClick={handleClose}>
            {result ? 'Close' : 'Cancel'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default GenerateFDV2Modal;
